<template>
    <modal-dialog
        ref="modal"
        label="AI Agent aanmaken"
        description="Maak hier je AI Agent aan."
        :size="3"
    >
        <flex-container
            :gap="Size.sm"
            no-wrap
            direction="column"
        >
            <create-assistent-form
                ref="form"
                @create-assistent="createAssistent"
                @create-brand="createBrand"
            >
                <template #actions="{ bindings }">
                    <button-text @click="modal?.closeOverlay(); bindings.resetForm()">
                        Annuleren
                    </button-text>
                </template>
            </create-assistent-form>
        </flex-container>
    </modal-dialog>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import { ButtonState, type ButtonStateChangedEvent, type ExposedOverlay, NotificationBlock, Size, useNotify } from '@keyvue/one-ui';
    import CreateAssistentForm, { type ExposedCreateAssistentForm } from '@/ai/assistent/components/CreateAssistentForm.vue';
    import { AssistentId, AssistentRole, CreateAssistentCommand, CreateBrandCommand } from '@keyvue/one-api-client';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import useEventBus from '@/services/eventbus/composables/useEventBus';
    import AssistentCreatedEvent, { type AssistentCreatedEventParameters } from '@/ai/assistent/events/AssistentCreatedEvent';
    import { v4 } from 'uuid';
    import BrandCreatedEvent, { type BrandCreatedEventParameters } from '@/company/brand/events/BrandCreatedEvent';
    import { useI18n } from 'vue-i18n';
    import messages from '@/ai/assistent/components/CreateAssistentModal.messages.json';

    const { notify } = useNotify();
    const { client, currentCompany, currentUser } = useClient();
    const eventBus = useEventBus();
    const { t } = useI18n({ messages });

    const modal = ref<ExposedOverlay>();
    const form = ref<ExposedCreateAssistentForm>();

    onMounted(() => {
        modal.value?.addOverlayEventListener('close', () => form.value?.resetForm());
    });

    async function createAssistent(event: ButtonStateChangedEvent) {
        const formValues = form.value?.form;

        if (!formValues) {
            event.button.setState(ButtonState.content);

            notify({
                component: NotificationBlock,
                props: {
                    title: 'We konden de AI Agent niet opslaan',
                    text: 'Probeer het later nog een keer.',
                    color: 'red-500',
                },
                duration: 4000,
            });

            return;
        }

        const command = CreateAssistentCommand.create({
            brandId: formValues.brand?.id,
            id: new AssistentId(v4()),
            name: formValues.name,
            roles: selectedRoles.value ?? null,
            behaviorGoals: formValues.leadGoals,
            behaviorPersonality: formValues.personality,
            behaviorFormality: formValues.formality,
            behaviorAnswerLength: formValues.answerLength,
            behaviorAllowEmoticons: formValues.allowEmoticons,
            createdById: currentUser.value.id,
        });

        await client.dispatch(command);

        event.button.setState(ButtonState.content);

        notify({
            component: NotificationBlock,
            props: {
                title: 'Gelukt!',
                text: 'We hebben de nieuwe AI Agent opgeslagen.',
                color: 'green-500',
            },
            duration: 4000,
        });

        eventBus.dispatch<AssistentCreatedEventParameters>(
            AssistentCreatedEvent,
            { assistentId: command.id.value },
        );

        form.value?.resetForm();
        modal.value?.closeOverlay();
    }

    async function createBrand(event: ButtonStateChangedEvent) {
        const formValues = form.value?.form;

        if (!formValues || formValues.websiteUrl === null) {
            event.button.setState(ButtonState.content);

            notify({
                component: NotificationBlock,
                props: {
                    title: t('create_brand.notification.error.title'),
                    text: t('create_brand.notification.error.text'),
                    color: 'red-500',
                },
                duration: 4000,
            });

            return;
        }

        const command = new CreateBrandCommand(
            currentCompany.value.id.value,
            v4(),
            formValues.websiteUrl,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            formValues.websiteUrl,
            {
                salesUrl: '',
                purchaseUrl: '',
                valuationUrl: '',
                mortgageUrl: '',
                contactUrl: '',
            },
            true,
            null,
            null,
        );

        await client.dispatch(command);

        eventBus.dispatch<BrandCreatedEventParameters>(
            BrandCreatedEvent,
            { brandId: command.id },
        );

        notify({
            component: NotificationBlock,
            props: {
                title: t('create_brand.notification.success.title'),
                text: t('create_brand.notification.success.text'),
                color: 'green-500',
            },
            duration: 4000,
        });

        event.button.setState(ButtonState.content);
        form.value?.resetForm();
    }

    /** Solution for the stupid design choice on how to select multiple options */
    const selectedRoles = computed(() => {
        const formValues = form.value?.form;
        const roleString = formValues?.role?.value;
        const roleStringArray = roleString?.split(',') || [];

        return roleStringArray.map((role) => {
            if (AssistentRole.SERVICE.equals(role)) {
                return AssistentRole.SERVICE;
            }

            return AssistentRole.LEAD_GENERATION;
        });
    });

    defineExpose({
        overlay: modal,
    });
</script>
